import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Modal, InputGroup, FormControl } from 'react-bootstrap';

import {
  setAllMarkerPoints,
  setPlantingDataSource
} from 'reducers/landDevReducer';

const GenerateTemplate = () => {
  const rowData = [
    {
      id: 1,
      name: 'Oak',
      numberOfItems: null,
      price: 15
    },
    {
      id: 2,
      name: 'Yellow Poplar',
      numberOfItems: null,
      price: 4
    },
    {
      id: 3,
      name: 'Horse Chestnut',
      numberOfItems: null,
      price: 6
    },
    {
      id: 4,
      name: 'Solar Panel',
      numberOfItems: null,
      price: 2000
    },
    {
      id: 5,
      name: 'Wind Turbine',
      numberOfItems: null,
      price: 8500
    }
  ];

  const dispatch = useDispatch();

  const [modalShow, setModalShow] = useState(false);
  const [landPercent, setLandPercent] = useState(50);
  const [budget, setBudget] = useState(1000);
  const [budgetRequired, setBudgetRequired] = useState(1000);
  const [landuse, setLanduse] = useState(0);
  const [priceOfTree] = useState(30);
  const [feetApartForEachTrees, setFeetApartForEachTrees] = useState(30);
  const [numOfTreesBasedOnLand, setNumOfTreesBasedOnLand] = useState(0);

  const { selectedLandParcels } = useSelector(state => state.landDev);

  const handleLandPercentSliderChange = e => {
    setLandPercent(e.target.value);
    if (landPercent >= 99) {
      setLanduse(Number(selectedLandParcels[0].area.toFixed(3)));
    } else {
      setLanduse((landPercent / 100) * selectedLandParcels[0].area.toFixed(3));
    }

    // divide by 3.281 to convert from feet to meter
    // multiply by 10000 converts hectare to square metres
    // area of land / area occupied by each tree
    setNumOfTreesBasedOnLand(
      Math.floor(
        (landuse * 10000) / (Math.pow(feetApartForEachTrees / 3.281, 2) * 3.142)
      )
    );

    setBudgetRequired(numOfTreesBasedOnLand * priceOfTree);
  };

  const handleFeetApartSliderChange = e => {
    setFeetApartForEachTrees(e.target.value);

    setNumOfTreesBasedOnLand(
      Math.floor(
        (landuse * 10000) / (Math.pow(feetApartForEachTrees / 3.281, 2) * 3.142)
      )
    );

    setBudgetRequired(numOfTreesBasedOnLand * priceOfTree);
  };

  const isPointInsidePolygon = (point, vertices) => {
    const [testX, testY] = point;

    let inside = false;
    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
      const [xi, yi] = [vertices[i].lat, vertices[i].lng];
      const [xj, yj] = [vertices[j].lat, vertices[j].lng];

      const intersect =
        yi > testY !== yj > testY &&
        testX < ((xj - xi) * (testY - yi)) / (yj - yi) + xi;

      if (intersect) {
        inside = !inside;
      }
    }

    return inside;
  };

  const generateTemplate = () => {
    const numberOfItems = 20;
    const collectedPoints = {};

    if (selectedLandParcels.length !== 0) {
      selectedLandParcels.forEach((polygon, index) => {
        const xMin = polygon.xMin;
        const xMax = polygon.xMax;
        const yMin = polygon.yMin;
        const yMax = polygon.yMax;
        let pointCount = 0;

        while (pointCount < numberOfItems) {
          let isInside = false;
          let lat, lng;

          while (!isInside) {
            lat = yMin + Math.random() * (yMax - yMin);
            lng = xMax + Math.random() * (xMin - xMax);

            isInside = isPointInsidePolygon([lat, lng], polygon.vertices);
          }
          const itemType = Math.floor(Math.random() * 5 + 1); // Random tree type (temporary)
          const timestamp = Date.now();

          const points = {
            id: `${pointCount}_${itemType}_${timestamp}`,
            lat: lat,
            lng: lng,
            itemType: itemType,
            parcel: polygon.parcelId
          };

          dispatch(setAllMarkerPoints(points));
          if (!collectedPoints[itemType]) {
            collectedPoints[itemType] = []; // Initialize array for tree type if not exists
          }

          collectedPoints[itemType].push(points); // Add point to corresponding tree type array
          pointCount++;
        }

        const treeTypeCounts = Object.entries(collectedPoints).map(
          ([itemType, pointsArray]) => ({
            itemType: itemType,
            count: pointsArray.length
          })
        );

        treeTypeCounts.forEach(count => {
          const itemType = count.itemType.toString();
          const treeData = rowData.find(
            data => data.id.toString() === itemType
          );

          if (treeData) {
            const plantingDataSource = {
              ...treeData,
              numberOfItems: count.count,
              parcel: polygon.parcelId,
              id: `${treeData.id}_${index + 1}`,
              itemType: itemType,
              coordinates: selectedLandParcels[0].coordinates
            };
            dispatch(setPlantingDataSource(plantingDataSource));
          }
        });

        setModalShow(false);
      });
    }
  };

  return (
    <>
      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Generate Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="outer">
            <div>
              <InputGroup className="mb-3">
                <InputGroup.Text id="budget">Budget</InputGroup.Text>
                <FormControl
                  placeholder="Budget"
                  aria-label="Budget"
                  aria-describedby="basic-addon1"
                  onChange={event => {
                    setBudget(event.target.value);
                  }}
                  value={budget ? budget : ''}
                />
              </InputGroup>

              <br />

              <Form.Label>Timeline:</Form.Label>
              <Form.Select aria-label="Default select example">
                <option>Select timeline</option>
                <option value="1">&lt; 2 years</option>
                <option value="2">2 - 10 years</option>
                <option value="3"> &gt; 10 years</option>
              </Form.Select>

              <br />

              <Form.Label>
                Percentage of land for development: {landPercent}%
              </Form.Label>
              <Form.Range
                value={landPercent}
                name="landPercent"
                min="5"
                max="100"
                onChange={handleLandPercentSliderChange}
                className="custom-slider"
              />

              <Form.Label>
                Feet apart for each tree: {feetApartForEachTrees}
              </Form.Label>
              <Form.Range
                value={feetApartForEachTrees}
                name="feetApart"
                min="5"
                max="50"
                onChange={handleFeetApartSliderChange}
                className="custom-slider"
              />

              <div>
                Amount of Landuse: {landuse.toFixed(3)}/
                {selectedLandParcels[0].area.toFixed(3)} ha
              </div>
              <div>
                Number of trees based on land: {numOfTreesBasedOnLand} trees
              </div>
              <div>Budget needed: ${budgetRequired} </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn btn-danger"
            onClick={() => setModalShow(false)}
          >
            Close
          </Button>
          <Button className="btn btn-success" onClick={generateTemplate}>
            Generate
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="ag-theme-quartz">
        <button
          className="btn btn-primary mb-3"
          onClick={() => setModalShow(true)}
        >
          Generate Template
        </button>
      </div>
    </>
  );
};

GenerateTemplate.propTypes = {
  polygon: PropTypes.object,
  data: PropTypes.shape({
    id: PropTypes.number,
    numberOfItems: PropTypes.number,
    icon: PropTypes.object
  }),
  value: PropTypes.string,
  onGenerateReport: PropTypes.func,
  removeAllEditControlLayers: PropTypes.object,
  activePolygon: PropTypes.object
};

export default GenerateTemplate;
